import { useMemo, useState } from "react";
import { Link, styled } from "@mui/material";
import { GeoJSON } from "leaflet";
import { area } from "@turf/turf";

import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import {
  DrawType,
  Measure,
  MeasureConfLayer,
  MeasureConf,
  DrawState,
} from "types/types";

import { cutUnderlyingPolygons, fitBasePolygon } from "utils/DrawUtils";
import {
  getGeojsonFeature,
  resetPolygonLayerStyle,
  applyEditLayerStyle,
} from "utils/GeomanUtils";
import { latLonToLonLat } from "utils/TurfHelperUtils";

import { basePolygonLayerStyle } from "config/geometryStyles";
import { floor } from "lodash-es";

interface Props {
  measureConfigurations: MeasureConf[];
  basePolygon: GeoJSON;
  layer: MeasureConfLayer;
  measure: Measure;
  minArea: number;
  drawState: DrawState;
  updateDrawState: (drawType?: DrawType) => void;
  updateMeasureConfigurations: (updatedMeasureConfigs: MeasureConf[]) => void;
}
export function EditPolygon(props: Props) {
  const {
    basePolygon,
    measureConfigurations,
    layer,
    measure,
    minArea,
    drawState,
    updateDrawState,
    updateMeasureConfigurations,
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const prevLayerShape = useMemo(() => {
    return getGeojsonFeature(layer.geometryLayer);
  }, [layer.geometryLayer]);

  const { enqueueSnackbar } = useSnackbar();

  function onEdit() {
    const layerToEdit = layer.geometryLayer;
    updateDrawState("edit");
    setIsEditing(true);
    //@ts-ignore
    layerToEdit.pm.toggleEdit({
      allowSelfIntersection: false,
      limitMarkersToCount: 25,
      pathOptions: {
        ...basePolygonLayerStyle,
        zIndex: 9999,
      },
    });

    layerToEdit.on("pm:markerdragend", (e) => {
      e.markerEvent.target.unbindTooltip();
    });

    layerToEdit.on("pm:markerdragstart", (e) => {
      e.markerEvent.target.bindTooltip(t("label.areaSqM", { area: 0 }), {
        permanent: true,
      });
    });

    layerToEdit.on("pm:markerdrag	", (e) => {
      // @ts-ignore
      const tooltip = e.markerEvent.target.getTooltip();
      if (tooltip) {
        const polygon = getGeojsonFeature(e.layer);
        const sqM = floor(area(polygon), 2).toLocaleString("nl-BE");
        tooltip.setContent(t("label.areaSqM", { area: sqM }));
      }
    });

    layerToEdit.on("pm:update", (e) => {
      const editedLayer = e.layer;
      e.layer.openTooltip();
      const cutEditedLayer = fitBasePolygon(editedLayer, basePolygon);
      if (!cutEditedLayer) {
        // @ts-ignore
        editedLayer.setLatLngs(
          latLonToLonLat(prevLayerShape.geometry.coordinates)
        );
        enqueueSnackbar(t("label.polygonMustBeInStudyArea"), {
          variant: "error",
        });
        return;
      }

      const surfaceArea = area(cutEditedLayer);
      if (surfaceArea < minArea) {
        // @ts-ignore
        editedLayer.setLatLngs(
          latLonToLonLat(prevLayerShape.geometry.coordinates)
        );
        enqueueSnackbar(t("label.polygonCantBeSmallerThan", { minArea }), {
          variant: "error",
        });
        return;
      }

      const updatedMeasureConfigs = cutUnderlyingPolygons(
        editedLayer,
        measureConfigurations,
        minArea,
        layer.id
      );
      if (cutEditedLayer) {
        //@ts-ignore
        editedLayer.setLatLngs(
          // @ts-ignore
          latLonToLonLat(cutEditedLayer.geometry.coordinates)
        );
      }
      updateMeasureConfigurations(updatedMeasureConfigs);
      return cleanUp();
    });
  }
  function cleanUp() {
    layer.geometryLayer.off("pm:update");
    layer.geometryLayer.off("pm:markerdrag");
    layer.geometryLayer.off("pm:markerdragstart");
    layer.geometryLayer.off("pm:markerdragend");
  }

  function onSave() {
    //@ts-ignore
    layer.geometryLayer.pm.toggleEdit();
    updateDrawState("off");

    setIsEditing(false);
  }

  const { t } = useTranslation();
  return (
    <Styles>
      {isEditing && (
        <Link
          onClick={onSave}
          onMouseLeave={() =>
            resetPolygonLayerStyle(layer.geometryLayer, measure)
          }
        >
          {t("label.save")}
        </Link>
      )}
      {!isEditing && (
        <Link
          onMouseEnter={() => applyEditLayerStyle(layer.geometryLayer)}
          onMouseLeave={() =>
            resetPolygonLayerStyle(layer.geometryLayer, measure)
          }
          onClick={onEdit}
          className={`${
            drawState.drawType === "edit" ? "disabled" : "enabled"
          }`}
        >
          {t("label.edit")}
        </Link>
      )}
    </Styles>
  );
}

const Styles = styled("div")`
  .MuiLink-root {
    &.disabled {
      pointer-events: none;
      text-decoration: none;
      cursor: not-allowed;
      color: ${({ theme }) => theme.palette.grey[400]};
    }
  }
`;
